import { useSubscription } from '@apollo/client';
import { produce } from 'immer';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  WorkerStatusTypesEnum,
  WorkerStatusTypesLookup,
} from '@eluve/graphql-types';

import {
  listenAppointmentLlmJobsSubscription,
  listenAppointmentSummarizationProgressSubscription,
} from './operations';
import { appointmentActivityFragment } from './useAppointmentActivity';
import { appointmentHeaderDetailsFragment } from './useAppointmentHeaderDetails';

// Backstop only; the watch normally stops on terminal job status below. Sized
// above the pipeline's worst-case end-to-end time (retries + stale-lock redrive).
const SUMMARIZATION_WATCH_TIMEOUT_MS = 20 * 60 * 1000;

const TERMINAL_JOB_STATUSES = new Set<WorkerStatusTypesEnum>([
  WorkerStatusTypesLookup.COMPLETED,
  WorkerStatusTypesLookup.FAILED,
  WorkerStatusTypesLookup.FAILED_WORKER_TERMINATED,
  WorkerStatusTypesLookup.CANCELLED,
]);

export const useAppointmentSummarizationWatch = ({
  appointmentId,
  tenantId,
  timeoutMs = SUMMARIZATION_WATCH_TIMEOUT_MS,
  onSummaryCompleted,
}: {
  appointmentId: string;
  tenantId: string;
  timeoutMs?: number;
  onSummaryCompleted?: () => void;
}) => {
  // Cursor for the jobs stream; reset to "now" on each arm so a regenerate
  // streams only the generation watched, not a replay of the prior job.
  const [streamingDateStart, setStreamingDateStart] = useState(() =>
    new Date().toISOString(),
  );

  const [isWatchingForSummary, setIsWatchingForSummary] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onSummaryCompletedRef = useRef(onSummaryCompleted);
  onSummaryCompletedRef.current = onSummaryCompleted;

  const stopWatchingForSummary = useCallback(() => {
    setIsWatchingForSummary(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const data = useCompleteFragment({
    fragment: appointmentHeaderDetailsFragment,
    key: {
      id: appointmentId,
    },
    strict: false,
  });

  const isSigned = Boolean(data?.doctor_interaction?.noteSignedAt);
  const currentHumanOutputId = data?.humanOutputs?.[0]?.humanOutputId ?? null;

  // The human output present when this watch armed. A regenerate keeps the prior note
  // around, so we must check "a new output appeared since we armed", not "any output
  // exists", or the watch would disarm before the regenerate even starts.
  const baselineHumanOutputIdRef = useRef<string | null>(null);
  const latestHumanOutputIdRef = useRef(currentHumanOutputId);
  latestHumanOutputIdRef.current = currentHumanOutputId;

  const hasNewSummary =
    currentHumanOutputId !== null &&
    currentHumanOutputId !== baselineHumanOutputIdRef.current;

  const startWatchingForSummary = useCallback(() => {
    setIsWatchingForSummary(true);
    baselineHumanOutputIdRef.current = latestHumanOutputIdRef.current;
    setStreamingDateStart(new Date().toISOString());

    if (appointmentId) {
      cacheUtils.updateFragment(
        {
          fragment: appointmentActivityFragment,
          key: {
            id: appointmentId,
          },
        },
        (existing) => {
          if (!existing) {
            return existing;
          }
          return produce(existing, (draft) => {
            const optimisticJob = {
              jobId: 'optimistic',
              __typename: 'AppointmentLlmJobs' as const,
              createdAt: new Date().toISOString(),
              jobStatus: 'PENDING_DATA_UPLOAD' as const,
              llmOutputType: 'SOAP_NOTE' as const,
              numAttempts: 0,
            };
            // Preserve the server's `distinctOn: llmOutputType` invariant:
            // a regenerate creates a new jobId, so replace the prior
            // SOAP_NOTE row in place rather than pushing a second one.
            const existingSoapJobIndex = draft.appointment_llm_jobs.findIndex(
              (job) => job.llmOutputType === 'SOAP_NOTE',
            );
            if (existingSoapJobIndex !== -1) {
              draft.appointment_llm_jobs[existingSoapJobIndex] = optimisticJob;
            } else {
              draft.appointment_llm_jobs.push(optimisticJob);
            }
          });
        },
      );
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsWatchingForSummary(false);
    }, timeoutMs);
  }, [appointmentId, timeoutMs]);

  useEffect(
    function stopWatchingIfSummarized() {
      if (isWatchingForSummary && hasNewSummary) {
        stopWatchingForSummary();
      }
    },
    [isWatchingForSummary, hasNewSummary, stopWatchingForSummary],
  );

  useEffect(function cleanupTimeoutOnUnmount() {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const shouldSkipSubscription =
    !appointmentId ||
    !tenantId ||
    isSigned ||
    !isWatchingForSummary ||
    hasNewSummary;

  useSubscription(listenAppointmentSummarizationProgressSubscription, {
    variables: {
      tenantId: tenantId,
      appointmentId: appointmentId,
    },
    skip: shouldSkipSubscription,
  });

  useSubscription(listenAppointmentLlmJobsSubscription, {
    variables: {
      tenantId: tenantId,
      appointmentId: appointmentId,
      initialValue: streamingDateStart,
    },
    skip: shouldSkipSubscription,
    onData: ({ data }) => {
      const jobUpdates = data.data?.appointmentLlmJobsStream;
      const soapNoteUpdate = jobUpdates?.find(
        (job) => job.llmOutputType === 'SOAP_NOTE',
      );
      if (soapNoteUpdate) {
        cacheUtils.updateFragment(
          {
            fragment: appointmentActivityFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }
            const final = produce(existing, (draft) => {
              const optimisticData = draft?.appointment_llm_jobs.findIndex(
                (job) => job.jobId === 'optimistic',
              );
              if (optimisticData !== -1) {
                draft.appointment_llm_jobs.splice(optimisticData, 1);
              }
              const existingJobIndex = draft.appointment_llm_jobs.findIndex(
                (job) => job.jobId === soapNoteUpdate.jobId,
              );
              if (existingJobIndex !== -1) {
                draft.appointment_llm_jobs[existingJobIndex] = soapNoteUpdate;
              } else {
                // Preserve the server's `distinctOn: llmOutputType` invariant:
                // a regenerate creates a new jobId, so replace the prior
                // SOAP_NOTE row in place rather than pushing a second one.
                const existingSoapJobIndex =
                  draft.appointment_llm_jobs.findIndex(
                    (job) => job.llmOutputType === soapNoteUpdate.llmOutputType,
                  );
                if (existingSoapJobIndex !== -1) {
                  draft.appointment_llm_jobs[existingSoapJobIndex] =
                    soapNoteUpdate;
                } else {
                  draft.appointment_llm_jobs.push(soapNoteUpdate);
                }
              }
            });
            return final;
          },
        );

        if (TERMINAL_JOB_STATUSES.has(soapNoteUpdate.jobStatus)) {
          if (soapNoteUpdate.jobStatus === WorkerStatusTypesLookup.COMPLETED) {
            onSummaryCompletedRef.current?.();
          }
          stopWatchingForSummary();
        }
      }
    },
  });

  return {
    startWatchingForSummary,
    stopWatchingForSummary,
    isWatching: isWatchingForSummary,
  };
};
